import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Check, ChevronsUpDown, User } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useOwner, useOwnerList } from "@/hooks/useOwners";
import type { WizardValues } from "./schema";

export const OwnerStep = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WizardValues>();
  const ownerId = watch("ownerId");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: list } = useOwnerList({
    search: search || undefined,
    limit: 10,
  });
  const { data: selected } = useOwner(ownerId);
  const error = errors.ownerId?.message;

  return (
    <div className="space-y-2">
      <Label>
        Owner<span className="text-destructive ml-0.5">*</span>
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between h-auto py-2.5"
            aria-invalid={!!error}
          >
            {selected ? (
              <span className="flex items-center gap-2 text-left">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>
                  <span className="font-medium">{selected.name}</span>
                  <span className="text-muted-foreground text-xs ml-2">
                    {selected.phone}
                  </span>
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                Search owner by name or phone…
              </span>
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50 ml-2 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search owners…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No owners found.</CommandEmpty>
              <CommandGroup>
                {(list?.data ?? []).map((o) => (
                  <CommandItem
                    key={o.id}
                    value={o.id}
                    onSelect={() => {
                      setValue("ownerId", o.id, { shouldValidate: true });
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        ownerId === o.id ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div>
                      <div className="font-medium">{o.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {o.phone} · {o._count?.businesses ?? 0} business(es)
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error ? (
        <p className="text-xs text-destructive">{error as string}</p>
      ) : selected ? (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-0.5">
          <div>
            <span className="text-muted-foreground">Phone: </span>
            {selected.phone}
          </div>
          {selected.email && (
            <div>
              <span className="text-muted-foreground">Email: </span>
              {selected.email}
            </div>
          )}
          {selected.whatsapp && (
            <div>
              <span className="text-muted-foreground">WhatsApp: </span>
              {selected.whatsapp}
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Pick the owner who will manage this business.
        </p>
      )}
    </div>
  );
};
